// ─── Panel HAUS · Admin ─────────────────────────────────────────────────────
// Dashboard reservado al asesor BREEAM (isAdmin) que centraliza el control
// del proyecto activo: KPIs, métricas por rama, últimas subidas de evidencias,
// alertas activas y actividad de accesos al portal.
//
// El componente se monta desde App.js cuando activeSection === 'panel-haus'.
// Sprint 1 · shell + BD.
// Sprint 2 · KPIs generales + métricas por rama.

import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  ChartPieSlice, ChartBar, Warning, UserCircle, FilesIcon,
  CheckCircle, ArrowsClockwise,
  UploadSimple, DownloadSimple, FileCsv, FileXls, MagnifyingGlass,
  ClockClockwise, SignIn, Users, CaretDown, CaretRight,
} from '@phosphor-icons/react'
import * as XLSX from 'xlsx'
import { supabase } from '../lib/supabase'
import { logAccess } from '../lib/logAccess'
import InvitacionesPanel from './InvitacionesPanel'
import ZonaNotificarNovedad from './ZonaNotificarNovedad'

// Sección colapsable con persistencia en localStorage (recuerda estado entre
// recargas por usuario y proyecto). Si no se pasa storageKey, no persiste.
function CollapsibleSection({ title, defaultOpen = false, storageKey, children }) {
  const key = storageKey ? `panel-haus-sec-${storageKey}` : null
  const initial = key && typeof window !== 'undefined'
    ? (window.localStorage.getItem(key) ?? (defaultOpen ? 'open' : 'closed')) === 'open'
    : defaultOpen
  const [open, setOpen] = useState(initial)
  function toggle() {
    const n = !open
    setOpen(n)
    if (key && typeof window !== 'undefined') window.localStorage.setItem(key, n ? 'open' : 'closed')
  }
  return (
    <div>
      <button
        onClick={toggle}
        style={{
          width: '100%', textAlign: 'left', background: open ? '#F0EAE4' : '#FAF7F3',
          border: `1px solid ${open ? '#D9CFC0' : '#E5DDC9'}`, borderRadius: 6,
          padding: '0.7rem 1rem', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.95rem', fontWeight: 700, color: '#2C2017',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          marginBottom: open ? '0.5rem' : 0,
          transition: 'background .15s, border-color .15s',
        }}
      >
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
        {title}
      </button>
      {open && <div>{children}</div>}
    </div>
  )
}

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A',
  bg: '#FFFFFF', bgAlt: '#FAF7F3',
}
const CARD = { background: C.bg, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }
const FONT = "'DM Sans', sans-serif"

const RAMAS_ORDER = ['Diseño', 'Instalaciones', 'Contratista', 'HAUS']
const RAMA_LABEL  = { 'Diseño': 'Equipo de Diseño', 'Instalaciones': 'Ingeniería de Instalaciones', 'Contratista': 'Contratista', 'HAUS': 'Consultoría (HAUS)' }
const ESTADO_LABEL = { pendiente: 'Pendiente', en_revision: 'En revisión', auditado: 'Auditado' }
const DIAS_REVISION_LARGA = 7  // umbral para "en revisión desde hace mucho"
const DIAS_INACTIVIDAD    = 14 // umbral para considerar a un usuario inactivo

function Header({ projectName, onReload, loading }) {
  return (
    <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: 500, fontFamily: FONT }}>
          Panel HAUS · admin
        </div>
        <div style={{ fontSize: '1.2rem', fontWeight: 600, fontFamily: FONT }}>Control del proyecto</div>
        <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem', fontFamily: FONT }}>
          {projectName ? projectName + ' · ' : ''}Vista consolidada del avance, actividad y alertas.
        </div>
      </div>
      <button
        onClick={onReload}
        disabled={loading}
        style={{
          padding: '0.5rem 0.9rem', background: 'rgba(255,255,255,0.1)',
          color: '#F5F0EA', border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: '4px', cursor: loading ? 'wait' : 'pointer',
          fontFamily: FONT, fontSize: '0.78rem', fontWeight: 500,
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
        }}>
        <ArrowsClockwise size={13} /> {loading ? 'Cargando…' : 'Recargar'}
      </button>
    </div>
  )
}

// ── Utilidades ─────────────────────────────────────────────────────────────
function isVencida(fechaISO) {
  if (!fechaISO) return false
  const hoy = new Date(); hoy.setHours(0,0,0,0)
  const fin = new Date(fechaISO); fin.setHours(0,0,0,0)
  return fin < hoy
}

// ── Zona 1 · KPIs generales ────────────────────────────────────────────────
function KpiCard({ Icon, label, value, subLabel, color = C.text, borderColor }) {
  return (
    <div style={{
      background: C.bg, borderRadius: '8px',
      padding: '1rem 1.15rem',
      border: `1px solid ${borderColor || C.border}`,
      borderTop: `3px solid ${color}`,
      display: 'flex', flexDirection: 'column', gap: '0.35rem',
      minHeight: '110px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Icon size={16} color={color} weight="regular" />
        <div style={{ fontSize: '0.7rem', color: C.muted, fontFamily: FONT, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          {label}
        </div>
      </div>
      <div style={{ fontSize: '1.9rem', fontWeight: 700, color, fontFamily: FONT, lineHeight: 1 }}>
        {value}
      </div>
      {subLabel && (
        <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT }}>
          {subLabel}
        </div>
      )}
    </div>
  )
}

function ZonaKPIs({ fichas, totalDocs }) {
  const total   = fichas.length
  const pend    = fichas.filter(f => f.estado === 'pendiente').length
  const rev     = fichas.filter(f => f.estado === 'en_revision').length
  const aud     = fichas.filter(f => f.estado === 'auditado').length
  const avance  = total ? Math.round((aud / total) * 100) : 0
  const vencidas = fichas.filter(f => isVencida(f.fecha_limite) && f.estado !== 'auditado').length
  const sinResp  = fichas.filter(f => !f.responsable).length

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.8rem' }}>
      <KpiCard
        Icon={ChartPieSlice}
        label="Fichas totales"
        value={total}
        subLabel={`${pend} pdte · ${rev} rev · ${aud} aud`}
        color={C.text}
      />
      <KpiCard
        Icon={CheckCircle}
        label="Avance global"
        value={`${avance}%`}
        subLabel={`${aud} de ${total} auditadas`}
        color={C.olive}
      />
      <KpiCard
        Icon={Warning}
        label="Vencidas"
        value={vencidas}
        subLabel={vencidas ? 'requieren acción' : 'todo al día'}
        color={vencidas ? C.coral : C.olive}
        borderColor={vencidas ? C.coral : C.border}
      />
      <KpiCard
        Icon={UserCircle}
        label="Sin responsable"
        value={sinResp}
        subLabel={sinResp ? 'por asignar' : 'todas asignadas'}
        color={sinResp ? C.amber : C.olive}
        borderColor={sinResp ? C.amber : C.border}
      />
      <KpiCard
        Icon={FilesIcon}
        label="Documentos aportados"
        value={totalDocs}
        subLabel="por el equipo"
        color={C.text}
      />
    </div>
  )
}

// ── Zona 2 · Métricas por rama ─────────────────────────────────────────────
function BarraRama({ rama, fichas }) {
  const total = fichas.length
  const pend  = fichas.filter(f => f.estado === 'pendiente').length
  const rev   = fichas.filter(f => f.estado === 'en_revision').length
  const aud   = fichas.filter(f => f.estado === 'auditado').length
  const pct   = total ? Math.round((aud / total) * 100) : 0

  // Anchos porcentuales de cada segmento (suma <= 100).
  const wPend = total ? (pend / total) * 100 : 0
  const wRev  = total ? (rev  / total) * 100 : 0
  const wAud  = total ? (aud  / total) * 100 : 0

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '0.35rem',
      padding: '0.75rem 0', borderBottom: `1px dashed ${C.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <div style={{ fontSize: '0.86rem', fontWeight: 600, color: C.text, fontFamily: FONT, flex: 1 }}>
          {RAMA_LABEL[rama] || rama}
        </div>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.olive, fontFamily: FONT }}>
          {pct}%
        </div>
        <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT, minWidth: '60px', textAlign: 'right' }}>
          {aud}/{total}
        </div>
      </div>

      <div style={{
        display: 'flex', width: '100%', height: '14px',
        background: C.bgAlt, borderRadius: '3px', overflow: 'hidden',
        border: `1px solid ${C.border}`,
      }}>
        {wAud  > 0 && <div title={`${aud} auditadas`}   style={{ width: `${wAud}%`,  background: C.olive }} />}
        {wRev  > 0 && <div title={`${rev} en revisión`} style={{ width: `${wRev}%`,  background: C.amber }} />}
        {wPend > 0 && <div title={`${pend} pendientes`} style={{ width: `${wPend}%`, background: C.coral }} />}
      </div>

      <div style={{ display: 'flex', gap: '0.9rem', fontSize: '0.72rem', color: C.muted, fontFamily: FONT }}>
        <span><span style={{ color: C.coral, fontWeight: 700 }}>{pend}</span> pdtes</span>
        <span><span style={{ color: C.amber, fontWeight: 700 }}>{rev}</span> revisión</span>
        <span><span style={{ color: C.olive, fontWeight: 700 }}>{aud}</span> auditadas</span>
      </div>
    </div>
  )
}

function ZonaMetricasRama({ fichas }) {
  const porRama = new Map(RAMAS_ORDER.map(r => [r, []]))
  fichas.forEach(f => {
    if (porRama.has(f.rama)) porRama.get(f.rama).push(f)
  })

  // Resumen compacto por rama para mostrar sin desplegar: solo el % auditado
  // (indicador de avance real) y el total de fichas de la rama.
  const resumenPorRama = RAMAS_ORDER.map(r => {
    const list = porRama.get(r) || []
    if (list.length === 0) return null
    const auditadas = list.filter(f => f.estado === 'auditado').length
    const pct = Math.round((auditadas / list.length) * 100)
    return { rama: r, total: list.length, pct }
  }).filter(Boolean)

  return (
    <details style={{ ...CARD, padding: 0 }}>
      <summary style={{
        cursor: 'pointer', padding: '1rem 1.25rem',
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        listStyle: 'none',
      }}>
        <ChartBar size={18} color={C.olive} />
        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.text, fontFamily: FONT }}>
          Avance por rama
        </div>
        {/* Mini-badges por rama · visibles siempre, incluso plegado */}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', flex: 1, marginLeft: '0.5rem' }}>
          {resumenPorRama.map(x => (
            <span key={x.rama} title={`${x.rama} · ${x.pct}% auditado · ${x.total} fichas`} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
              padding: '0.15rem 0.55rem', borderRadius: 12,
              background: C.bgAlt, border: `1px solid ${C.border}`,
              fontSize: '0.72rem', color: C.text, fontFamily: FONT,
            }}>
              <span style={{ fontWeight: 600 }}>{RAMA_LABEL[x.rama] || x.rama}</span>
              <span style={{
                fontWeight: 700,
                color: x.pct >= 75 ? C.olive : (x.pct >= 40 ? C.amber : C.coral),
              }}>
                {x.pct}%
              </span>
            </span>
          ))}
        </div>
        <span style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT }}>
          desplegar detalle
        </span>
      </summary>
      <div style={{ padding: '0 1.25rem 1.25rem' }}>
        {RAMAS_ORDER.map(r => {
          const list = porRama.get(r) || []
          if (list.length === 0) return null
          return <BarraRama key={r} rama={r} fichas={list} />
        })}
        {/* Leyenda de colores */}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.7rem', color: C.muted, fontFamily: FONT }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: '10px', height: '10px', background: C.olive, borderRadius: '2px' }} /> Auditadas
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: '10px', height: '10px', background: C.amber, borderRadius: '2px' }} /> En revisión
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: '10px', height: '10px', background: C.coral, borderRadius: '2px' }} /> Pendientes
          </span>
        </div>
      </div>
    </details>
  )
}

// ── Exportación CSV / XLSX ─────────────────────────────────────────────────
function exportarCSV(filename, rows) {
  if (!rows.length) return
  const cols = Object.keys(rows[0])
  const esc = v => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n')
  const bom = '﻿'  // para que Excel interprete UTF-8
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}
function exportarXLSX(filename, rows, sheetName = 'Datos') {
  if (!rows.length) return
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)
  XLSX.writeFile(wb, filename)
}

function BotonesExport({ rows, base }) {
  const disabled = !rows || rows.length === 0
  const style = {
    padding: '0.32rem 0.65rem', borderRadius: '4px',
    background: 'white', color: disabled ? C.muted : C.text,
    border: `1px solid ${C.border}`, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: FONT, fontSize: '0.72rem', fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
  }
  return (
    <div style={{ display: 'inline-flex', gap: '0.35rem' }}>
      <button disabled={disabled} onClick={() => exportarCSV(`${base}.csv`, rows)} style={style}>
        <FileCsv size={12} /> CSV
      </button>
      <button disabled={disabled} onClick={() => exportarXLSX(`${base}.xlsx`, rows)} style={style}>
        <FileXls size={12} /> XLSX
      </button>
    </div>
  )
}

// ── Zona 3 · Últimas subidas ───────────────────────────────────────────────
function ZonaUltimasSubidas({ subidas, users, fichasIndex, lastSeen }) {
  const [filtroUser, setFiltroUser] = useState('todos')
  const [filtroRama, setFiltroRama] = useState('todas')
  const [q, setQ]                   = useState('')

  const nuevasCount = useMemo(() => {
    if (!lastSeen) return 0
    return subidas.filter(d => d.uploaded_at && new Date(d.uploaded_at) > lastSeen).length
  }, [subidas, lastSeen])

  const nombreUsuario = (uid) => {
    const p = users[uid]
    if (p?.full_name) return p.full_name
    if (p?.email)     return p.email
    return uid ? uid.slice(0, 8) + '…' : '—'
  }

  const enriched = useMemo(() => subidas.map(d => {
    const f = fichasIndex[d.pliego_requisito_id] || {}
    return {
      ...d,
      user_label: nombreUsuario(d.user_id),
      rama: f.rama || '—',
      criteria_code: f.criteria_code || '—',
      criteria_name: f.criteria_name || '',
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [subidas, fichasIndex, users])

  const nq = (q || '').toLowerCase().trim()
  const filtered = enriched.filter(d => {
    if (filtroUser !== 'todos' && d.user_id !== filtroUser) return false
    if (filtroRama !== 'todas' && d.rama !== filtroRama)   return false
    if (nq && !(
      (d.filename || '').toLowerCase().includes(nq) ||
      (d.criteria_code || '').toLowerCase().includes(nq) ||
      (d.criteria_name || '').toLowerCase().includes(nq) ||
      (d.user_label   || '').toLowerCase().includes(nq)
    )) return false
    return true
  })

  const exportRows = filtered.map(d => ({
    fecha: d.uploaded_at ? new Date(d.uploaded_at).toISOString() : '',
    usuario: d.user_label,
    rama: d.rama,
    criterio: d.criteria_code,
    ficha: d.criteria_name,
    archivo: d.filename,
    peso_bytes: d.size_bytes || '',
  }))

  const abrirDoc = async (path) => {
    if (!path) return
    const { data } = await supabase.storage.from('evidences').createSignedUrl(path, 3600)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  const usersUnicos = Array.from(new Set(subidas.map(d => d.user_id))).filter(Boolean)

  return (
    <div style={{ ...CARD }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <UploadSimple size={18} color={C.olive} />
        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.text, fontFamily: FONT, flex: 1 }}>
          Últimas subidas del equipo
          <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 500, color: C.muted }}>
            {filtered.length} / {subidas.length}
          </span>
          {nuevasCount > 0 && (
            <span style={{
              marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 700,
              color: '#fff', background: C.coral, padding: '2px 8px',
              borderRadius: '10px', letterSpacing: '0.02em',
            }} title={`${nuevasCount} subida${nuevasCount !== 1 ? 's' : ''} desde tu última visita`}>
              {nuevasCount} nueva{nuevasCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <BotonesExport rows={exportRows} base={`subidas-${new Date().toISOString().slice(0,10)}`} />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
          padding: '0.28rem 0.55rem',
          border: `1px solid ${q ? C.olive : C.border}`,
          borderRadius: '4px', background: 'white',
        }}>
          <MagnifyingGlass size={12} color={q ? C.olive : C.muted} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar archivo/criterio/persona"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT, fontSize: '0.74rem', color: C.text, width: '220px' }} />
        </div>
        <label style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT, display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
          Persona:
          <select value={filtroUser} onChange={e => setFiltroUser(e.target.value)}
            style={{ padding: '0.28rem 0.5rem', borderRadius: '4px', border: `1px solid ${C.border}`, background: 'white', fontFamily: FONT, fontSize: '0.72rem', color: C.text }}>
            <option value="todos">Todas</option>
            {usersUnicos.map(uid => <option key={uid} value={uid}>{nombreUsuario(uid)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT, display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
          Rama:
          <select value={filtroRama} onChange={e => setFiltroRama(e.target.value)}
            style={{ padding: '0.28rem 0.5rem', borderRadius: '4px', border: `1px solid ${C.border}`, background: 'white', fontFamily: FONT, fontSize: '0.72rem', color: C.text }}>
            <option value="todas">Todas</option>
            {RAMAS_ORDER.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: C.muted, background: C.bgAlt, borderRadius: '6px', border: `1px dashed ${C.border}`, fontFamily: FONT, fontSize: '0.82rem' }}>
          No hay subidas que coincidan con los filtros.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '480px', overflowY: 'auto' }}>
          {filtered.map(d => {
            const esNueva = lastSeen && d.uploaded_at && new Date(d.uploaded_at) > lastSeen
            return (
            <div key={d.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.5rem 0.7rem',
              border: esNueva ? `1px solid ${C.coral}` : `1px solid ${C.border}`,
              borderLeft: esNueva ? `3px solid ${C.coral}` : `1px solid ${C.border}`,
              borderRadius: '4px',
              background: esNueva ? '#FFF7F5' : C.bgAlt,
              fontFamily: FONT,
            }}>
              {esNueva && (
                <span style={{
                  fontSize: '0.62rem', fontWeight: 700, color: '#fff',
                  background: C.coral, padding: '1px 6px', borderRadius: '8px',
                  letterSpacing: '0.02em',
                }}>NUEVO</span>
              )}
              <span style={{ fontSize: '0.7rem', color: C.muted, minWidth: '95px' }}>
                {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
              </span>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: C.text, minWidth: '55px' }}>
                {d.criteria_code}
              </span>
              <span style={{ fontSize: '0.72rem', color: C.muted, minWidth: '90px', textAlign: 'left' }}>
                {d.rama}
              </span>
              <span style={{ flex: 1, fontSize: '0.78rem', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.filename}
              </span>
              <span style={{ fontSize: '0.72rem', color: C.muted, minWidth: '160px', textAlign: 'right' }}>
                {d.user_label}
              </span>
              <button onClick={() => abrirDoc(d.storage_path)} title="Descargar" style={{
                padding: '0.25rem 0.55rem', borderRadius: '4px', background: 'white', color: C.text,
                border: `1px solid ${C.border}`, cursor: 'pointer', fontFamily: FONT, fontSize: '0.7rem',
                fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              }}>
                <DownloadSimple size={11} /> Ver
              </button>
            </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Zona 4 · Alertas activas ───────────────────────────────────────────────
function AlertaCard({ Icon, title, color, items, emptyMsg, extraCol }) {
  return (
    <div style={{
      background: C.bg, borderRadius: '8px',
      border: `1px solid ${C.border}`,
      borderTop: `3px solid ${color}`,
      padding: '1rem 1.15rem',
      display: 'flex', flexDirection: 'column', gap: '0.6rem',
      minHeight: '260px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Icon size={16} color={color} weight="regular" />
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: C.text, fontFamily: FONT, flex: 1 }}>
          {title}
        </div>
        <span style={{
          fontSize: '0.7rem', fontWeight: 700, color, padding: '2px 8px',
          background: 'white', border: `1px solid ${color}`, borderRadius: '10px',
        }}>
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: '0.76rem', color: C.muted, fontStyle: 'italic', fontFamily: FONT }}>
          {emptyMsg}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '260px', overflowY: 'auto' }}>
          {items.map(it => (
            <div key={it.id} style={{
              padding: '0.4rem 0.55rem', border: `1px solid ${C.border}`,
              borderLeft: `3px solid ${color}`, borderRadius: '3px',
              background: C.bgAlt, fontFamily: FONT,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: C.text }}>{it.criteria_code}</span>
                <span style={{ fontSize: '0.68rem', color: C.muted }}>{it.rama}</span>
              </div>
              <div style={{ fontSize: '0.74rem', color: C.text, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {it.criteria_name}
              </div>
              {extraCol && (
                <div style={{ fontSize: '0.68rem', color, marginTop: '2px', fontWeight: 600 }}>
                  {extraCol(it)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ZonaAlertas({ fichas }) {
  const hoy = new Date(); hoy.setHours(0,0,0,0)
  const vencidas = fichas.filter(f => isVencida(f.fecha_limite) && f.estado !== 'auditado')
  const sinResp  = fichas.filter(f => !f.responsable)
  const revLarga = fichas.filter(f => {
    if (f.estado !== 'en_revision' || !f.updated_at) return false
    const upd = new Date(f.updated_at); upd.setHours(0,0,0,0)
    const dias = Math.round((hoy - upd) / (24*3600*1000))
    return dias > DIAS_REVISION_LARGA
  })

  const diasEnRev = (f) => {
    const upd = new Date(f.updated_at); upd.setHours(0,0,0,0)
    return Math.round((hoy - upd) / (24*3600*1000))
  }
  const diasVencida = (f) => {
    const fin = new Date(f.fecha_limite); fin.setHours(0,0,0,0)
    return Math.round((hoy - fin) / (24*3600*1000))
  }

  return (
    <div style={{ ...CARD }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.9rem' }}>
        <Warning size={18} color={C.coral} />
        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.text, fontFamily: FONT }}>
          Alertas activas
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.8rem' }}>
        <AlertaCard
          Icon={Warning} color={C.coral}
          title="Vencidas sin auditar"
          items={vencidas}
          emptyMsg="Ninguna ficha vencida. Todo al día."
          extraCol={(f) => `Vencida hace ${diasVencida(f)} d · ${ESTADO_LABEL[f.estado] || f.estado}`}
        />
        <AlertaCard
          Icon={ClockClockwise} color={C.amber}
          title={`En revisión > ${DIAS_REVISION_LARGA} días`}
          items={revLarga}
          emptyMsg="Ninguna ficha atascada en revisión."
          extraCol={(f) => `En revisión desde hace ${diasEnRev(f)} d`}
        />
        <AlertaCard
          Icon={UserCircle} color={C.amber}
          title="Sin responsable"
          items={sinResp}
          emptyMsg="Todas las fichas tienen responsable."
          extraCol={(f) => f.fase_exigencia || ''}
        />
      </div>
    </div>
  )
}

// ── Zona 5 · Actividad de accesos ──────────────────────────────────────────
function ZonaAccesos({ accesos: accesosAll, users, allUsersOfProject }) {
  const [rango, setRango]   = useState('30')       // '7' | '30' | '90' | 'todos'
  const [filtroUser, setFiltroUser] = useState('todos')
  const [filtroSec, setFiltroSec]   = useState('todas')
  const [myUid, setMyUid]           = useState(null)

  // Detectamos la sesión del admin en ejecución para excluirla del listado.
  // Un asesor HAUS entra al panel varias veces al día · sus eventos ahogan la
  // señal de actividad de los invitados externos, que es lo que importa.
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setMyUid(data?.user?.id || null))
  }, [])

  // Excluimos: (a) al usuario actual, (b) todo role admin/asesor, y (c) cualquier
  // email @haus-hb.com. Esto último captura cuentas de prueba internas de HAUS
  // (Gisell tiene una segunda cuenta invitada como arquitectura para testing,
  // y en el futuro se podrían crear más). Los invitados externos reales del
  // proyecto (Coderch, arumgroup, ingenierías) usan otros dominios.
  const uidsAdminHaus = useMemo(() => {
    const s = new Set()
    if (myUid) s.add(myUid)
    Object.entries(users || {}).forEach(([uid, p]) => {
      if (p?.role === 'admin' || p?.role === 'asesor') s.add(uid)
      const email = (p?.email || '').toLowerCase()
      if (email.endsWith('@haus-hb.com') || email.endsWith('@haushealthybuildings.com')) s.add(uid)
    })
    return s
  }, [myUid, users])

  const accesos = useMemo(
    () => accesosAll.filter(a => !uidsAdminHaus.has(a.user_id)),
    [accesosAll, uidsAdminHaus]
  )

  const nombreUsuario = (uid) => {
    const p = users[uid]
    if (p?.full_name) return p.full_name
    if (p?.email)     return p.email
    return uid ? uid.slice(0, 8) + '…' : '—'
  }
  const orgUsuario = (uid) => users[uid]?.organization || ''
  const emailUsuario = (uid) => users[uid]?.email || ''

  // Filtro por rango temporal · calculado en cliente sobre los ya cargados
  const hoyMs = Date.now()
  const diasMs = 24 * 3600 * 1000
  const enRango = a => {
    if (rango === 'todos') return true
    const dias = parseInt(rango, 10)
    return (hoyMs - new Date(a.occurred_at).getTime()) < dias * diasMs
  }
  const filtered = accesos.filter(a => {
    if (!enRango(a)) return false
    if (filtroUser !== 'todos' && a.user_id !== filtroUser) return false
    if (filtroSec  !== 'todas' && (a.section || '') !== filtroSec) return false
    return true
  })

  // Usuarios activos (últimos 7 días) vs inactivos (>14 días sin entrar)
  const ultimaVisitaPorUsuario = new Map()  // uid → timestamp más reciente
  for (const a of accesos) {
    const t = new Date(a.occurred_at).getTime()
    if (!ultimaVisitaPorUsuario.has(a.user_id) || t > ultimaVisitaPorUsuario.get(a.user_id)) {
      ultimaVisitaPorUsuario.set(a.user_id, t)
    }
  }
  const usuariosActivos = Array.from(ultimaVisitaPorUsuario.entries())
    .filter(([, t]) => (hoyMs - t) < 7 * diasMs)
    .map(([uid, t]) => ({ uid, t }))
  const usuariosInactivos = (allUsersOfProject || [])
    .filter(uid => !uidsAdminHaus.has(uid))  // no listar admins HAUS
    .map(uid => ({ uid, t: ultimaVisitaPorUsuario.get(uid) || null }))
    .filter(u => u.t == null || (hoyMs - u.t) > DIAS_INACTIVIDAD * diasMs)

  // Secciones únicas para el dropdown
  const seccionesUnicas = Array.from(new Set(accesos.map(a => a.section || 'app'))).sort()
  const usersConAccesos = Array.from(new Set(accesos.map(a => a.user_id))).filter(Boolean)

  const exportRows = filtered.map(a => ({
    fecha: new Date(a.occurred_at).toISOString(),
    usuario: nombreUsuario(a.user_id),
    email: emailUsuario(a.user_id),
    organizacion: orgUsuario(a.user_id),
    seccion: a.section || '',
    rama: a.rama || '',
  }))

  return (
    <div style={{ ...CARD }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <SignIn size={18} color={C.olive} />
        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.text, fontFamily: FONT, flex: 1 }}>
          Actividad de accesos
          <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 500, color: C.muted }}>
            {filtered.length} / {accesos.length} eventos
          </span>
        </div>
        <BotonesExport rows={exportRows} base={`accesos-${new Date().toISOString().slice(0,10)}`} />
      </div>

      {/* Mini-KPIs de actividad */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', marginBottom: '0.9rem' }}>
        <div style={{ padding: '0.6rem 0.85rem', border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.olive}`, borderRadius: '4px', background: C.bgAlt }}>
          <div style={{ fontSize: '0.68rem', color: C.muted, fontFamily: FONT, textTransform: 'uppercase', fontWeight: 600 }}>Activos (7d)</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: C.olive, fontFamily: FONT }}>{usuariosActivos.length}</div>
        </div>
        <div style={{ padding: '0.6rem 0.85rem', border: `1px solid ${C.border}`, borderLeft: `3px solid ${usuariosInactivos.length ? C.amber : C.olive}`, borderRadius: '4px', background: C.bgAlt }}>
          <div style={{ fontSize: '0.68rem', color: C.muted, fontFamily: FONT, textTransform: 'uppercase', fontWeight: 600 }}>Inactivos (&gt;{DIAS_INACTIVIDAD}d)</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: usuariosInactivos.length ? C.amber : C.olive, fontFamily: FONT }}>{usuariosInactivos.length}</div>
        </div>
        <div style={{ padding: '0.6rem 0.85rem', border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.text}`, borderRadius: '4px', background: C.bgAlt }}>
          <div style={{ fontSize: '0.68rem', color: C.muted, fontFamily: FONT, textTransform: 'uppercase', fontWeight: 600 }}>Total registrado</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: C.text, fontFamily: FONT }}>{accesos.length}</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT, display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
          Rango:
          <select value={rango} onChange={e => setRango(e.target.value)}
            style={{ padding: '0.28rem 0.5rem', borderRadius: '4px', border: `1px solid ${C.border}`, background: 'white', fontFamily: FONT, fontSize: '0.72rem', color: C.text }}>
            <option value="7">Últimos 7 días</option>
            <option value="30">Últimos 30 días</option>
            <option value="90">Últimos 90 días</option>
            <option value="todos">Todo</option>
          </select>
        </label>
        <label style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT, display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
          Persona:
          <select value={filtroUser} onChange={e => setFiltroUser(e.target.value)}
            style={{ padding: '0.28rem 0.5rem', borderRadius: '4px', border: `1px solid ${C.border}`, background: 'white', fontFamily: FONT, fontSize: '0.72rem', color: C.text }}>
            <option value="todos">Todas</option>
            {usersConAccesos.map(uid => <option key={uid} value={uid}>{nombreUsuario(uid)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FONT, display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}>
          Sección:
          <select value={filtroSec} onChange={e => setFiltroSec(e.target.value)}
            style={{ padding: '0.28rem 0.5rem', borderRadius: '4px', border: `1px solid ${C.border}`, background: 'white', fontFamily: FONT, fontSize: '0.72rem', color: C.text }}>
            <option value="todas">Todas</option>
            {seccionesUnicas.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      {/* Lista de accesos */}
      {filtered.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: C.muted, background: C.bgAlt, borderRadius: '6px', border: `1px dashed ${C.border}`, fontFamily: FONT, fontSize: '0.82rem' }}>
          No hay accesos registrados con estos filtros.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '400px', overflowY: 'auto' }}>
          {filtered.map(a => (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.5rem 0.7rem', border: `1px solid ${C.border}`,
              borderRadius: '4px', background: C.bgAlt, fontFamily: FONT,
            }}>
              <span style={{ fontSize: '0.7rem', color: C.muted, minWidth: '115px' }}>
                {new Date(a.occurred_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
              <span style={{ flex: 1, fontSize: '0.78rem', color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nombreUsuario(a.user_id)}
                {orgUsuario(a.user_id) && <span style={{ color: C.muted, fontWeight: 400 }}> · {orgUsuario(a.user_id)}</span>}
              </span>
              <span style={{ fontSize: '0.72rem', color: C.muted, minWidth: '90px', textAlign: 'right' }}>
                {a.section || 'app'}
              </span>
              {a.rama && (
                <span style={{ fontSize: '0.7rem', color: C.olive, minWidth: '95px', textAlign: 'right', fontWeight: 600 }}>
                  {a.rama}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Bloque de usuarios inactivos */}
      {usuariosInactivos.length > 0 && (
        <div style={{ marginTop: '1rem', paddingTop: '0.9rem', borderTop: `1px dashed ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
            <Users size={14} color={C.amber} />
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.text, fontFamily: FONT }}>
              Usuarios inactivos · &gt;{DIAS_INACTIVIDAD} días sin entrar
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {usuariosInactivos.map(u => {
              const nunca = u.t == null
              const dias = nunca ? null : Math.round((hoyMs - u.t) / diasMs)
              return (
                <div key={u.uid} style={{
                  padding: '0.35rem 0.6rem', border: `1px solid ${C.amber}`,
                  borderRadius: '4px', background: 'white', fontFamily: FONT,
                  display: 'inline-flex', flexDirection: 'column',
                }}>
                  <span style={{ fontSize: '0.74rem', color: C.text, fontWeight: 600 }}>
                    {nombreUsuario(u.uid)}
                  </span>
                  <span style={{ fontSize: '0.66rem', color: C.amber, fontWeight: 600 }}>
                    {nunca ? 'nunca ha entrado' : `hace ${dias} d`}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Componente principal ───────────────────────────────────────────────────
export default function PanelHausAdmin({ projectId, projectName }) {
  const [fichas, setFichas] = useState([])
  const [subidas, setSubidas] = useState([])
  const [accesos, setAccesos] = useState([])
  const [users, setUsers] = useState({})   // { user_id: { full_name, email, organization } }
  const [allUsersOfProject, setAllUsersOfProject] = useState([])  // uids con acceso teórico al proyecto
  const [totalDocs, setTotalDocs] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Timestamp de la última visita del admin al Panel HAUS. Se congela al montar
  // el componente para poder marcar como "nuevas" las subidas posteriores a esa
  // fecha durante la sesión actual. Después, actualizamos user_profiles con now()
  // para que la próxima sesión use este momento como referencia.
  const [lastSeen, setLastSeen] = useState(null)

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true); setError(null)
    try {
      // Capturar el last_seen previo ANTES de actualizarlo, para poder pintar
      // las novedades. Si es la primera vez que se abre el panel, usamos ahora
      // menos 24h como default (evita marcar todas las subidas históricas).
      const { data: authUser } = await supabase.auth.getUser()
      const uid = authUser?.user?.id
      if (uid) {
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('panel_haus_last_seen')
          .eq('user_id', uid)
          .maybeSingle()
        const previo = prof?.panel_haus_last_seen
          ? new Date(prof.panel_haus_last_seen)
          : new Date(Date.now() - 24 * 60 * 60 * 1000)
        setLastSeen(previo)
        // Actualizar el timestamp para la próxima visita (no bloqueante)
        supabase.from('user_profiles')
          .update({ panel_haus_last_seen: new Date().toISOString() })
          .eq('user_id', uid)
          .then(() => {})
      }

      // Fichas del pliego (incluye updated_at para detectar revisiones largas
      // y fase_exigencia para la tarjeta de "sin responsable").
      const { data: fRows, error: fErr } = await supabase
        .from('pliego_requisitos')
        .select('id,rama,estado,responsable,fecha_limite,criteria_code,criteria_name,fase_exigencia,updated_at')
        .eq('project_id', projectId)
      if (fErr) throw fErr
      setFichas(fRows || [])

      // Subidas de evidencias (últimas 200 · suficiente para el panel).
      const { data: dRows, error: dErr } = await supabase
        .from('pliego_documentos')
        .select('id,pliego_requisito_id,user_id,filename,storage_path,size_bytes,uploaded_at')
        .eq('project_id', projectId)
        .order('uploaded_at', { ascending: false })
        .limit(200)
      if (dErr) throw dErr
      setSubidas(dRows || [])
      setTotalDocs(dRows?.length || 0)

      // Accesos registrados (todos los del proyecto + los de nivel app del
      // asesor · para tener contexto de actividad). Solo admin ve todo por RLS.
      const { data: aRows, error: aErr } = await supabase
        .from('access_log')
        .select('id,user_id,project_id,section,rama,occurred_at')
        .or(`project_id.eq.${projectId},project_id.is.null`)
        .order('occurred_at', { ascending: false })
        .limit(500)
      if (aErr) throw aErr
      setAccesos(aRows || [])

      // Usuarios con acceso al proyecto · propietario + shared_with.
      const { data: proyRow } = await supabase
        .from('projects')
        .select('user_id, shared_with')
        .eq('id', projectId)
        .maybeSingle()
      const projUids = new Set()
      if (proyRow?.user_id) projUids.add(proyRow.user_id)
      for (const uid of (proyRow?.shared_with || [])) projUids.add(uid)
      setAllUsersOfProject(Array.from(projUids))

      // Perfiles · union de quienes suben docs, quienes acceden, y quienes
      // tienen permiso teórico al proyecto.
      const uids = new Set([
        ...((dRows || []).map(d => d.user_id).filter(Boolean)),
        ...((aRows || []).map(a => a.user_id).filter(Boolean)),
        ...projUids,
      ])
      if (uids.size > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('user_id, full_name, organization')
          .in('user_id', Array.from(uids))
        setUsers(Object.fromEntries((profiles || []).map(p => [p.user_id, p])))
      } else {
        setUsers({})
      }
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [projectId])

  const fichasIndex = useMemo(() => {
    const idx = {}
    for (const f of fichas) idx[f.id] = f
    return idx
  }, [fichas])

  useEffect(() => {
    if (projectId) logAccess({ section: 'panel-haus', projectId })
    load()
  }, [projectId, load])

  if (!projectId) {
    return (
      <div style={{ padding: '2rem', color: C.muted, fontFamily: FONT }}>
        Selecciona un proyecto para ver el Panel HAUS.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: FONT }}>
      <Header projectName={projectName} onReload={load} loading={loading} />

      {error && (
        <div style={{ background: '#FFF1ED', border: `1px solid ${C.coral}`, borderRadius: 6, padding: '0.55rem 0.9rem', color: C.coral, fontSize: '0.82rem', fontFamily: FONT }}>
          Error cargando datos: {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...CARD, textAlign: 'center', color: C.muted, fontFamily: FONT }}>
          Cargando datos del proyecto…
        </div>
      ) : (
        <>
          <CollapsibleSection title="KPIs del proyecto" defaultOpen={true} storageKey="kpis">
            <ZonaKPIs fichas={fichas} totalDocs={totalDocs} />
          </CollapsibleSection>
          <CollapsibleSection title="Métricas por rama" defaultOpen={true} storageKey="metricas">
            <ZonaMetricasRama fichas={fichas} />
          </CollapsibleSection>
          <CollapsibleSection title="Últimas subidas" defaultOpen={false} storageKey="subidas">
            <ZonaUltimasSubidas subidas={subidas} users={users} fichasIndex={fichasIndex} lastSeen={lastSeen} />
          </CollapsibleSection>
          <CollapsibleSection title="Alertas" defaultOpen={false} storageKey="alertas">
            <ZonaAlertas fichas={fichas} />
          </CollapsibleSection>
          <CollapsibleSection title="Accesos al portal" defaultOpen={false} storageKey="accesos">
            <ZonaAccesos accesos={accesos} users={users} allUsersOfProject={allUsersOfProject} />
          </CollapsibleSection>
          <CollapsibleSection title="Invitaciones al pliego" defaultOpen={false} storageKey="invitaciones">
            <InvitacionesPanel projectId={projectId} projectName={projectName} />
          </CollapsibleSection>
          <CollapsibleSection title="Notificar novedad" defaultOpen={false} storageKey="notificar">
            <ZonaNotificarNovedad projectId={projectId} projectName={projectName} />
          </CollapsibleSection>
        </>
      )}
    </div>
  )
}
